import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Trash2, Mail, Phone, Calendar, LogOut, MessageSquare, FileText, User, Plus, DollarSign, X, Send, CheckCircle, Pencil, Upload, CalendarDays, Clock, ClipboardList, Image, Download, Settings } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';

import { API } from '../config/api';

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function PhotoPicker({ selectedIds, onChange }) {
  const [photos, setPhotos] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchPhotos = useCallback(() => {
    axios.get(`${API}/admin/photos`, { headers: getAuthHeaders(), withCredentials: true })
      .then((res) => setPhotos(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (expanded) fetchPhotos();
  }, [expanded, fetchPhotos]);

  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((p) => p !== id) : [...selectedIds, id]);
  };

  const handleDeviceUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newIds = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'Uncategorized');
      formData.append('caption', file.name.replace(/\.[^.]+$/, ''));
      try {
        const res = await axios.post(`${API}/admin/photos/upload`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        newIds.push(res.data.id);
      } catch { /* skip failed uploads */ }
    }
    if (newIds.length > 0) {
      onChange([...selectedIds, ...newIds]);
      fetchPhotos();
      toast.success(`${newIds.length} photo${newIds.length > 1 ? 's' : ''} uploaded & attached`);
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div>
      <button type="button" onClick={() => setExpanded(!expanded)} className="text-sky-700 text-sm font-medium hover:underline flex items-center gap-1">
        <Image className="w-3.5 h-3.5" /> {expanded ? 'Hide photos' : `Attach job photos${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
      </button>
      {expanded && (
        <div className="mt-2 border border-sky-100 rounded-xl p-3 bg-sky-50/50">
          <div className="flex items-center gap-2 mb-2">
            <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all ${uploading ? 'bg-slate-100 text-slate-400' : 'bg-sky-100 text-sky-700 hover:bg-sky-200'}`}>
              <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Upload from device'}
              <input type="file" accept="image/*" multiple onChange={handleDeviceUpload} disabled={uploading} className="hidden" />
            </label>
            {selectedIds.length > 0 && <span className="text-xs text-slate-500">{selectedIds.length} selected</span>}
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-36 overflow-y-auto">
              {photos.map((p) => (
                <button key={p.id} type="button" onClick={() => toggle(p.id)}
                  className={`relative rounded-lg overflow-hidden h-16 border-2 transition-all ${selectedIds.includes(p.id) ? 'border-sky-600 ring-2 ring-sky-300' : 'border-transparent hover:border-sky-200'}`}>
                  <img src={`${API}/photos/serve/${p.id}`} alt={p.caption || ''} className="w-full h-full object-cover" />
                  {selectedIds.includes(p.id) && (
                    <div className="absolute inset-0 bg-sky-600/30 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-white drop-shadow" /></div>
                  )}
                </button>
              ))}
            </div>
          )}
          {photos.length === 0 && !uploading && (
            <p className="text-xs text-slate-400">No gallery photos yet. Use the button above to upload from your device.</p>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-sky-100 p-5 shadow-sm">
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-slate-400 text-xs">{label}</p>
          <p className="font-heading text-xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function ReviewsTab({ reviews, onDelete }) {
  if (reviews.length === 0) return <p className="text-slate-400 text-center py-8">No reviews yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-reviews-list">
      {reviews.map((r) => (
        <div key={r.id} className="bg-white rounded-xl border border-sky-100 p-4 shadow-sm" data-testid={`admin-review-${r.id}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-900 text-sm">{r.name}</span>
                {r.service && <span className="text-slate-400 text-xs">({r.service})</span>}
                <div className="flex gap-0.5 ml-auto">
                  {[1,2,3,4,5].map((s) => (
                    <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`} />
                  ))}
                </div>
              </div>
              <p className="text-slate-600 text-sm">{r.text}</p>
              <p className="text-slate-300 text-xs mt-1">{new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
            </div>
            <button onClick={() => onDelete(r.id)} data-testid={`admin-delete-review-${r.id}`} className="ml-3 p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactsTab({ contacts, onDelete }) {
  if (contacts.length === 0) return <p className="text-slate-400 text-center py-8">No contact submissions yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-contacts-list">
      {contacts.map((c) => (
        <div key={c.id} className="bg-white rounded-xl border border-sky-100 p-4 shadow-sm" data-testid={`admin-contact-${c.id}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-900 text-sm">{c.name}</span>
                <span className="bg-sky-100 text-sky-700 text-xs font-medium px-2 py-0.5 rounded-full">{c.service}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-1">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                {c.preferred_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{c.preferred_date}</span>}
              </div>
              <p className="text-slate-600 text-sm bg-sky-50 rounded-lg px-3 py-2 mt-1">{c.message}</p>
              <p className="text-slate-300 text-xs mt-1">{new Date(c.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <button onClick={() => onDelete(c.id)} data-testid={`admin-delete-contact-${c.id}`} className="ml-3 p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


function BookingsTab({ bookings, onDelete, onRefresh }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({ customer_name: '', email: '', phone: '', address: '', date: '', slot: '', notes: '', status: '' });
  const [saving, setSaving] = useState(false);
  const [blockDate, setBlockDate] = useState('');
  const [blockSlot, setBlockSlot] = useState('FULL');
  const [blockReason, setBlockReason] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [showBlocks, setShowBlocks] = useState(false);

  const fetchBlocks = async () => {
    try {
      const res = await axios.get(`${API}/admin/availability-blocks`, { headers: getAuthHeaders(), withCredentials: true });
      setBlocks(res.data);
    } catch {}
  };

  const handleBlock = async () => {
    if (!blockDate) { toast.error('Select a date'); return; }
    try {
      await axios.post(`${API}/admin/availability-blocks`, { date: blockDate, slot: blockSlot, reason: blockReason || null }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Blocked ${blockSlot === 'FULL' ? 'full day' : blockSlot} on ${blockDate}`);
      setBlockDate(''); setBlockReason('');
      fetchBlocks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to block'); }
  };

  const handleUnblock = async (date, slot) => {
    try {
      await axios.delete(`${API}/admin/availability-blocks?date=${date}&slot=${slot}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Unblocked');
      fetchBlocks();
    } catch { toast.error('Failed to unblock'); }
  };

  useState(() => { fetchBlocks(); }, []);

  const openEdit = (b) => {
    setEditBooking(b);
    setEditForm({ customer_name: b.customer_name||'', email: b.email||'', phone: b.phone||'', address: b.address||'', date: b.date||'', slot: b.slot||'', notes: b.notes||'', status: b.status||'confirmed' });
    setEditOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/bookings/${editBooking.id}`, editForm, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Booking updated!');
      setEditOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const statusColor = (s) => {
    if (s === 'confirmed') return 'bg-green-100 text-green-700';
    if (s === 'completed') return 'bg-sky-100 text-sky-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-600';
  };

  const sorted = [...bookings].sort((a, b) => a.date > b.date ? -1 : 1);

  return (
    <div>
      {/* Availability Management */}
      <div className="mb-6">
        <button onClick={() => { setShowBlocks(!showBlocks); if (!showBlocks) fetchBlocks(); }} className="text-sm font-medium text-sky-700 hover:underline mb-3 block">
          {showBlocks ? 'Hide' : 'Manage'} Availability
        </button>
        {showBlocks && (
          <div className="bg-sky-50 rounded-xl p-4 mb-4 space-y-3">
            <p className="text-sm font-semibold text-slate-700">Block Time Slots</p>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Date</label>
                <Input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className="w-40" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Slot</label>
                <select value={blockSlot} onChange={(e) => setBlockSlot(e.target.value)} className="h-10 rounded-md border border-sky-200 px-3 text-sm">
                  <option value="FULL">Full Day</option>
                  <option value="AM">Morning</option>
                  <option value="PM">Afternoon</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Reason (optional)</label>
                <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="e.g., Holiday" className="w-36" />
              </div>
              <Button onClick={handleBlock} className="bg-red-500 text-white rounded-full px-4 hover:bg-red-600 text-sm h-10">Block</Button>
            </div>
            {blocks.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-medium text-slate-500">Blocked slots:</p>
                {blocks.map((b, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm">
                    <span className="text-slate-700">{b.date} — <span className="font-medium">{b.slot === 'AM' ? 'Morning' : 'Afternoon'}</span> {b.reason && <span className="text-slate-400">({b.reason})</span>}</span>
                    <button onClick={() => handleUnblock(b.date, b.slot)} className="text-red-400 hover:text-red-600 text-xs font-medium">Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {sorted.length === 0 ? (
        <p className="text-slate-400 text-center py-8">No bookings yet.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-bookings-list">
          {sorted.map((b) => (
            <div key={b.id} className="bg-white rounded-xl border border-sky-100 p-4 shadow-sm" data-testid={`admin-booking-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-heading font-semibold text-slate-900">{b.booking_number}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(b.status)}`}>{b.status}</span>
                    <span className="text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full font-medium">{b.slot === 'AM' ? 'Morning' : 'Afternoon'}</span>
                    <span className="text-xs text-slate-500 ml-auto">
                      {new Date(b.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <p className="text-slate-700 text-sm">{b.customer_name}</p>
                  <p className="text-slate-400 text-xs">{b.address}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {b.items?.map((item, i) => (
                      <span key={i} className="text-xs bg-sky-50 text-slate-600 px-2 py-0.5 rounded">{item.service} — ${item.price?.toFixed(2)}</span>
                    ))}
                  </div>
                  <div className="flex gap-3 text-xs text-slate-400 mt-1">
                    {b.email && <span>{b.email}</span>}
                    {b.phone && <span>{b.phone}</span>}
                    <span className="font-semibold text-sky-700">${b.total?.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 ml-3">
                  <button onClick={() => openEdit(b)} data-testid={`admin-edit-booking-${b.id}`} className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors" title="Edit booking"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(b.id)} data-testid={`admin-delete-booking-${b.id}`} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-booking-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit Booking {editBooking?.booking_number}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Customer</label><Input value={editForm.customer_name} onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})} /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Email</label><Input value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-700 block mb-1">Address</label><Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Date</label><Input type="date" value={editForm.date} onChange={(e) => setEditForm({...editForm, date: e.target.value})} /></div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Slot</label>
                <Select value={editForm.slot} onValueChange={(v) => setEditForm({...editForm, slot: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="AM">Morning</SelectItem><SelectItem value="PM">Afternoon</SelectItem></SelectContent></Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Status</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div><label className="text-xs font-medium text-slate-700 block mb-1">Notes</label><Textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} rows={2} /></div>
            <Button type="submit" disabled={saving} className="w-full bg-sky-700 text-white rounded-full hover:bg-sky-600">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function EstimatesTab({ estimates, onDelete, onRefresh }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [form, setForm] = useState({ customer_name: '', address: '', email: '', phone: '', tax_rate: 0, notes: '', valid_days: 60 });
  const [items, setItems] = useState([{ description: '', amount: '' }]);
  const [editForm, setEditForm] = useState({ customer_name: '', address: '', email: '', phone: '', tax_rate: 0, notes: '', valid_days: 60 });
  const [editItems, setEditItems] = useState([{ description: '', amount: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [photoIds, setPhotoIds] = useState([]);
  const [editPhotoIds, setEditPhotoIds] = useState([]);

  const addItem = () => setItems([...items, { description: '', amount: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => { const u = [...items]; u[i] = { ...u[i], [field]: val }; setItems(u); };

  const addEditItem = () => setEditItems([...editItems, { description: '', amount: '' }]);
  const removeEditItem = (i) => setEditItems(editItems.filter((_, idx) => idx !== i));
  const updateEditItem = (i, field, val) => { const u = [...editItems]; u[i] = { ...u[i], [field]: val }; setEditItems(u); };

  const openEdit = (est) => {
    setEditingEstimate(est);
    setEditForm({
      customer_name: est.customer_name || '', address: est.address || '', email: est.email || '',
      phone: est.phone || '', tax_rate: est.tax_rate || 0, notes: est.notes || '', valid_days: est.valid_days || 60,
    });
    setEditItems((est.items || []).map((it) => ({ description: it.description, amount: String(it.amount) })));
    setEditPhotoIds(est.photo_ids || []);
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editForm.customer_name || !editForm.address) { toast.error('Fill in customer name and address.'); return; }
    const validItems = editItems.filter((it) => it.description && it.amount);
    if (validItems.length === 0) { toast.error('Add at least one line item.'); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/estimates/${editingEstimate.id}`, {
        customer_name: editForm.customer_name, address: editForm.address, email: editForm.email || null,
        phone: editForm.phone || null, items: validItems.map((it) => ({ description: it.description, amount: parseFloat(it.amount) })),
        tax_rate: parseFloat(editForm.tax_rate) || 0, notes: editForm.notes, valid_days: parseInt(editForm.valid_days) || 60,
        photo_ids: editPhotoIds,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Estimate updated!');
      setEditOpen(false);
      setEditingEstimate(null);
      onRefresh();
    } catch { toast.error('Failed to update estimate.'); }
    finally { setSubmitting(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.address) { toast.error('Fill in customer name and address.'); return; }
    const validItems = items.filter((it) => it.description && it.amount);
    if (validItems.length === 0) { toast.error('Add at least one line item.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/estimates`, {
        customer_name: form.customer_name, address: form.address, email: form.email || null,
        phone: form.phone || null, items: validItems.map((it) => ({ description: it.description, amount: parseFloat(it.amount) })),
        tax_rate: parseFloat(form.tax_rate) || 0, notes: form.notes, valid_days: parseInt(form.valid_days) || 60,
        photo_ids: photoIds,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Estimate created!');
      setCreateOpen(false);
      setForm({ customer_name: '', address: '', email: '', phone: '', tax_rate: 0, notes: '', valid_days: 60 });
      setItems([{ description: '', amount: '' }]);
      setPhotoIds([]);
      onRefresh();
    } catch { toast.error('Failed to create estimate.'); }
    finally { setSubmitting(false); }
  };

  const handleSend = async (est) => {
    if (!est.email) { toast.error('No customer email on this estimate.'); return; }
    setSendingId(est.id);
    try {
      await axios.post(`${API}/admin/estimates/${est.id}/send`, { origin_url: window.location.origin }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Estimate sent to ${est.email}`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send.'); }
    finally { setSendingId(null); }
  };

  const handleConvert = async (est) => {
    if (!window.confirm(`Convert ${est.estimate_number} to an invoice? This will create a new unpaid invoice with the same details.`)) return;
    setConvertingId(est.id);
    try {
      const res = await axios.post(`${API}/admin/estimates/${est.id}/convert-to-invoice`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Converted to invoice ${res.data.invoice.invoice_number}`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to convert.'); }
    finally { setConvertingId(null); }
  };

  const handleDownloadPdf = async (est) => {
    try {
      const res = await axios.get(`${API}/admin/estimates/${est.id}/pdf`, {
        headers: getAuthHeaders(), withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${est.estimate_number || 'estimate'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const statusColor = (s) => {
    if (s === 'accepted') return 'bg-green-100 text-green-700';
    if (s === 'expired') return 'bg-red-100 text-red-700';
    if (s === 'converted') return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <p className="text-slate-500 text-sm">{estimates.length} estimate(s)</p>
        <Button onClick={() => setCreateOpen(true)} data-testid="admin-create-estimate-btn" className="bg-sky-700 text-white rounded-full px-4 hover:bg-sky-600 text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> New Estimate
        </Button>
      </div>

      {estimates.length === 0 ? (
        <p className="text-slate-400 text-center py-8">No estimates yet.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-estimates-list">
          {estimates.map((est) => (
            <div key={est.id} className="bg-white rounded-xl border border-sky-100 p-4 shadow-sm" data-testid={`admin-estimate-${est.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-semibold text-slate-900">{est.estimate_number}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(est.status)}`}>{est.status}</span>
                    <span className="font-heading font-bold text-sky-700 ml-auto">${est.total?.toFixed(2)}</span>
                  </div>
                  <p className="text-slate-700 text-sm">{est.customer_name}</p>
                  <p className="text-slate-400 text-xs">{est.address}</p>
                  <div className="flex gap-3 text-xs text-slate-400 mt-1">
                    {est.email && <span>{est.email}</span>}
                    <span>{new Date(est.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {est.emailed_at && <span className="text-green-500 font-medium">Emailed</span>}
                    {est.converted_to_invoice && <span className="text-blue-600 font-medium">→ {est.converted_to_invoice}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 ml-3">
                  <button onClick={() => openEdit(est)} className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors" title="Edit estimate" data-testid={`admin-edit-estimate-${est.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDownloadPdf(est)} className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors" title="Download PDF" data-testid={`admin-pdf-estimate-${est.id}`}><Download className="w-4 h-4" /></button>
                  {est.status !== 'converted' && (
                    <button onClick={() => handleConvert(est)} disabled={convertingId === est.id} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-50" title="Convert to invoice" data-testid={`admin-convert-estimate-${est.id}`}><FileText className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => handleSend(est)} disabled={sendingId === est.id} className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors disabled:opacity-50" title="Send estimate" data-testid={`admin-send-estimate-${est.id}`}><Send className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(est.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Delete" data-testid={`admin-delete-estimate-${est.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Estimate Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-estimate-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Create Estimate</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Customer Name *</label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="John Smith" /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-700 block mb-1">Address *</label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Naples, FL" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Phone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Tax Rate (%)</label><Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Valid (days)</label><Input type="number" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} /></div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-2">Line Items *</label>
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="flex-1" />
                  <Input type="number" step="0.01" placeholder="Amount" value={item.amount} onChange={(e) => updateItem(i, 'amount', e.target.value)} className="w-28" />
                  {items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="p-2 text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button type="button" onClick={addItem} className="text-sky-700 text-sm font-medium hover:underline">+ Add Line Item</button>
            </div>
            <div><label className="text-xs font-medium text-slate-700 block mb-1">Notes</label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." rows={2} /></div>
            <PhotoPicker selectedIds={photoIds} onChange={setPhotoIds} />
            <Button type="submit" disabled={submitting} className="w-full bg-sky-700 text-white rounded-full hover:bg-sky-600">{submitting ? 'Creating...' : 'Create Estimate'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Estimate Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-estimate-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit Estimate {editingEstimate?.estimate_number}</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Customer Name *</label><Input data-testid="edit-est-name" value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Email</label><Input data-testid="edit-est-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-700 block mb-1">Address *</label><Input data-testid="edit-est-address" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Phone</label><Input data-testid="edit-est-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Tax Rate (%)</label><Input data-testid="edit-est-tax" type="number" step="0.01" value={editForm.tax_rate} onChange={(e) => setEditForm({ ...editForm, tax_rate: e.target.value })} /></div>
              <div><label className="text-xs font-medium text-slate-700 block mb-1">Valid (days)</label><Input data-testid="edit-est-valid" type="number" value={editForm.valid_days} onChange={(e) => setEditForm({ ...editForm, valid_days: e.target.value })} /></div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-2">Line Items *</label>
              {editItems.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input data-testid={`edit-est-item-desc-${i}`} placeholder="Description" value={item.description} onChange={(e) => updateEditItem(i, 'description', e.target.value)} className="flex-1" />
                  <Input data-testid={`edit-est-item-amt-${i}`} type="number" step="0.01" placeholder="Amount" value={item.amount} onChange={(e) => updateEditItem(i, 'amount', e.target.value)} className="w-28" />
                  {editItems.length > 1 && <button type="button" onClick={() => removeEditItem(i)} className="p-2 text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button type="button" onClick={addEditItem} className="text-sky-700 text-sm font-medium hover:underline" data-testid="edit-est-add-item">+ Add Line Item</button>
            </div>
            <div><label className="text-xs font-medium text-slate-700 block mb-1">Notes</label><Textarea data-testid="edit-est-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Optional notes..." rows={2} /></div>
            <PhotoPicker selectedIds={editPhotoIds} onChange={setEditPhotoIds} />
            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} data-testid="edit-est-save-btn" className="flex-1 bg-sky-700 text-white rounded-full hover:bg-sky-600">{submitting ? 'Saving...' : 'Save Changes'}</Button>
              {editingEstimate?.email && (
                <Button
                  type="button"
                  disabled={submitting || sendingId === editingEstimate?.id}
                  data-testid="edit-est-save-send-btn"
                  className="flex-1 bg-emerald-600 text-white rounded-full hover:bg-emerald-500"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!editForm.customer_name || !editForm.address) { toast.error('Fill in customer name and address.'); return; }
                    const validItems = editItems.filter((it) => it.description && it.amount);
                    if (validItems.length === 0) { toast.error('Add at least one line item.'); return; }
                    setSubmitting(true);
                    try {
                      await axios.put(`${API}/admin/estimates/${editingEstimate.id}`, {
                        customer_name: editForm.customer_name, address: editForm.address, email: editForm.email || null,
                        phone: editForm.phone || null, items: validItems.map((it) => ({ description: it.description, amount: parseFloat(it.amount) })),
                        tax_rate: parseFloat(editForm.tax_rate) || 0, notes: editForm.notes, valid_days: parseInt(editForm.valid_days) || 60,
                        photo_ids: editPhotoIds,
                      }, { headers: getAuthHeaders(), withCredentials: true });
                      setSendingId(editingEstimate.id);
                      await axios.post(`${API}/admin/estimates/${editingEstimate.id}/send`, { origin_url: window.location.origin }, { headers: getAuthHeaders(), withCredentials: true });
                      toast.success(`Estimate updated & sent to ${editForm.email}`);
                      setEditOpen(false);
                      setEditingEstimate(null);
                      onRefresh();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save & send.'); }
                    finally { setSubmitting(false); setSendingId(null); }
                  }}
                >
                  <Send className="w-4 h-4 mr-1.5" /> Save & Send
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function InvoicesTab({ invoices, onDelete, onRefresh }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [form, setForm] = useState({ customer_name: '', address: '', email: '', phone: '', tax_rate: 0, notes: '' });
  const [items, setItems] = useState([{ description: '', amount: '' }]);
  const [editForm, setEditForm] = useState({ customer_name: '', address: '', email: '', phone: '', tax_rate: 0, notes: '', status: '' });
  const [editItems, setEditItems] = useState([{ description: '', amount: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [markingId, setMarkingId] = useState(null);
  const [photoIds, setPhotoIds] = useState([]);
  const [editPhotoIds, setEditPhotoIds] = useState([]);

  const addItem = () => setItems([...items, { description: '', amount: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    setItems(updated);
  };

  const handleSendInvoice = async (inv) => {
    if (!inv.email) {
      toast.error('Invoice has no customer email.');
      return;
    }
    setSendingId(inv.id);
    try {
      const res = await axios.post(`${API}/admin/invoices/${inv.id}/send`, {
        origin_url: window.location.origin,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message || `Invoice sent to ${inv.email}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice.');
    } finally {
      setSendingId(null);
    }
  };

  const handleMarkPaid = async (inv) => {
    setMarkingId(inv.id);
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/mark-paid`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`${inv.invoice_number} marked as paid`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid.');
    } finally {
      setMarkingId(null);
    }
  };

  const openEditDialog = (inv) => {
    setEditInvoice(inv);
    setEditForm({
      customer_name: inv.customer_name || '',
      address: inv.address || '',
      email: inv.email || '',
      phone: inv.phone || '',
      tax_rate: inv.tax_rate || 0,
      notes: inv.notes || '',
      status: inv.status || 'unpaid',
    });
    setEditItems(inv.items && inv.items.length > 0 ? inv.items.map(i => ({ description: i.description, amount: String(i.amount) })) : [{ description: '', amount: '' }]);
    setEditPhotoIds(inv.photo_ids || []);
    setEditOpen(true);
  };

  const handleDownloadInvoicePdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, {
        headers: getAuthHeaders(), withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number || 'invoice'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editForm.customer_name || !editForm.address) {
      toast.error('Please fill in customer name and address.');
      return;
    }
    const validItems = editItems.filter((it) => it.description && it.amount);
    if (validItems.length === 0) {
      toast.error('Add at least one line item.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/invoices/${editInvoice.id}`, {
        customer_name: editForm.customer_name,
        address: editForm.address,
        email: editForm.email || null,
        phone: editForm.phone || null,
        items: validItems.map((it) => ({ description: it.description, amount: parseFloat(it.amount) })),
        tax_rate: parseFloat(editForm.tax_rate) || 0,
        notes: editForm.notes,
        status: editForm.status,
        photo_ids: editPhotoIds,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Invoice updated!');
      setEditOpen(false);
      setEditInvoice(null);
      onRefresh();
    } catch {
      toast.error('Failed to update invoice.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.address || !form.email) {
      toast.error('Please fill in customer name, address, and email.');
      return;
    }
    const validItems = items.filter((it) => it.description && it.amount);
    if (validItems.length === 0) {
      toast.error('Add at least one line item.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/invoices`, {
        customer_name: form.customer_name,
        address: form.address,
        email: form.email,
        phone: form.phone || null,
        items: validItems.map((it) => ({ description: it.description, amount: parseFloat(it.amount) })),
        tax_rate: parseFloat(form.tax_rate) || 0,
        notes: form.notes,
        photo_ids: photoIds,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Invoice created!');
      setCreateOpen(false);
      setForm({ customer_name: '', address: '', email: '', phone: '', tax_rate: 0, notes: '' });
      setItems([{ description: '', amount: '' }]);
      setPhotoIds([]);
      onRefresh();
    } catch {
      toast.error('Failed to create invoice.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (s) => {
    if (s === 'paid') return 'bg-green-100 text-green-700';
    if (s === 'overdue') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <p className="text-slate-500 text-sm">{invoices.length} invoice(s)</p>
        <div className="flex gap-2">
          <Button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/admin/seed-estimates`, {}, { headers: getAuthHeaders(), withCredentials: true });
                toast.success(res.data.message);
                onRefresh();
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to import'); }
            }}
            data-testid="seed-estimates-btn"
            className="bg-amber-500 text-white rounded-full px-4 hover:bg-amber-600 text-sm"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Import Estimates
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="admin-create-invoice-btn" className="bg-sky-700 text-white rounded-full px-4 hover:bg-sky-600 text-sm">
            <Plus className="w-4 h-4 mr-1.5" /> New Invoice
          </Button>
        </div>
      </div>

      {invoices.length === 0 ? (
        <p className="text-slate-400 text-center py-8">No invoices yet. Create one or import estimates.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-invoices-list">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-white rounded-xl border border-sky-100 p-4 shadow-sm" data-testid={`admin-invoice-${inv.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-semibold text-slate-900">{inv.invoice_number}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(inv.status)}`}>
                      {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                    </span>
                    <span className="font-heading font-bold text-sky-700 ml-auto">${inv.total.toFixed(2)}</span>
                  </div>
                  <p className="text-slate-700 text-sm">{inv.customer_name}</p>
                  <p className="text-slate-400 text-xs">{inv.address}</p>
                  <div className="flex gap-3 text-xs text-slate-400 mt-1">
                    <span>{inv.email}</span>
                    <span>{new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {inv.emailed_at && <span className="text-green-500 font-medium">Emailed</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 ml-3">
                  {inv.status !== 'paid' && (
                    <button
                      onClick={() => handleMarkPaid(inv)}
                      disabled={markingId === inv.id}
                      data-testid={`admin-mark-paid-${inv.id}`}
                      className="p-1.5 rounded-lg hover:bg-green-50 text-slate-400 hover:text-green-600 transition-colors disabled:opacity-50"
                      title="Mark as paid"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleSendInvoice(inv)}
                    disabled={sendingId === inv.id}
                    data-testid={`admin-send-invoice-${inv.id}`}
                    className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors disabled:opacity-50"
                    title={inv.status === 'paid' ? 'Send receipt' : 'Send invoice to customer'}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEditDialog(inv)}
                    data-testid={`admin-edit-invoice-${inv.id}`}
                    className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors"
                    title="Edit invoice"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDownloadInvoicePdf(inv)}
                    data-testid={`admin-pdf-invoice-${inv.id}`}
                    className="p-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors"
                    title="Download PDF"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(inv.id)} data-testid={`admin-delete-invoice-${inv.id}`} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Customer Name *</label>
                <Input data-testid="invoice-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="John Smith" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Email *</label>
                <Input type="email" data-testid="invoice-customer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Service Address *</label>
              <Input data-testid="invoice-customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Fort Myers, FL 33901" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Phone</label>
                <Input data-testid="invoice-customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(239) 555-0123" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Tax Rate (%)</label>
                <Input type="number" step="0.01" data-testid="invoice-tax-rate" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} placeholder="0" />
              </div>
            </div>

            {/* Line items */}
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-2">Line Items *</label>
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="flex-1" data-testid={`invoice-item-desc-${i}`} />
                  <Input type="number" step="0.01" placeholder="Amount" value={item.amount} onChange={(e) => updateItem(i, 'amount', e.target.value)} className="w-28" data-testid={`invoice-item-amount-${i}`} />
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="p-2 text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addItem} data-testid="invoice-add-item-btn" className="text-sky-700 text-sm font-medium hover:underline">
                + Add Line Item
              </button>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
              <Textarea data-testid="invoice-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." rows={2} />
            </div>
            <PhotoPicker selectedIds={photoIds} onChange={setPhotoIds} />
            <Button type="submit" disabled={submitting} data-testid="invoice-submit-btn" className="w-full bg-sky-700 text-white rounded-full hover:bg-sky-600">
              {submitting ? 'Creating...' : 'Create Invoice'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Invoice {editInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Customer Name *</label>
                <Input value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Email</label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Service Address *</label>
              <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Phone</label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Tax Rate (%)</label>
                <Input type="number" step="0.01" value={editForm.tax_rate} onChange={(e) => setEditForm({ ...editForm, tax_rate: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Status</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-2">Line Items *</label>
              {editItems.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Description" value={item.description} onChange={(e) => { const u = [...editItems]; u[i] = { ...u[i], description: e.target.value }; setEditItems(u); }} className="flex-1" />
                  <Input type="number" step="0.01" placeholder="Amount" value={item.amount} onChange={(e) => { const u = [...editItems]; u[i] = { ...u[i], amount: e.target.value }; setEditItems(u); }} className="w-28" />
                  {editItems.length > 1 && (
                    <button type="button" onClick={() => setEditItems(editItems.filter((_, idx) => idx !== i))} className="p-2 text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setEditItems([...editItems, { description: '', amount: '' }])} className="text-sky-700 text-sm font-medium hover:underline">
                + Add Line Item
              </button>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
            </div>
            <PhotoPicker selectedIds={editPhotoIds} onChange={setEditPhotoIds} />
            <Button type="submit" disabled={submitting} className="w-full bg-sky-700 text-white rounded-full hover:bg-sky-600">
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [caption, setCaption] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  const fetchPhotos = useCallback(async () => {
    try {
      const [photosRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/photos`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/photo-categories`),
      ]);
      setPhotos(photosRes.data);
      setCategories(catsRes.data);
    } catch { toast.error('Failed to load photos'); }
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setFiles((prev) => [...prev, ...selected]);
    setPreviews((prev) => [...prev, ...selected.map((f) => URL.createObjectURL(f))]);
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    previews.forEach((p) => URL.revokeObjectURL(p));
    setFiles([]);
    setPreviews([]);
    const el = document.getElementById('photo-upload-input');
    if (el) el.value = '';
  };

  const handleUpload = async () => {
    if (files.length === 0 || !selectedCategory) {
      toast.error('Select at least one photo and a category');
      return;
    }
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < files.length; i++) {
      setUploadProgress({ current: i + 1, total: files.length });
      const formData = new FormData();
      formData.append('file', files[i]);
      formData.append('category', selectedCategory);
      formData.append('caption', caption);
      try {
        await axios.post(`${API}/admin/photos/upload`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        successCount++;
      } catch {
        failCount++;
      }
    }
    if (successCount > 0) toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded`);
    if (failCount > 0) toast.error(`${failCount} upload${failCount > 1 ? 's' : ''} failed`);
    clearAll();
    setCaption('');
    setSelectedCategory('');
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    fetchPhotos();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo from the gallery?')) return;
    try {
      await axios.delete(`${API}/admin/photos/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch { toast.error('Failed to delete photo'); }
  };

  const filtered = filterCat === 'All' ? photos : photos.filter((p) => p.category === filterCat);

  return (
    <div data-testid="admin-photos-tab">
      {/* Upload form */}
      <div className="bg-white rounded-2xl border border-sky-100 shadow-sm p-5 mb-6">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-sky-600" /> Upload Photos
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Photos *</label>
            <input
              id="photo-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileChange}
              data-testid="photo-upload-input"
              className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 cursor-pointer"
            />
            {files.length > 0 && (
              <p className="text-xs text-slate-400 mt-1">{files.length} file{files.length > 1 ? 's' : ''} selected</p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Category *</label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="photo-category-select" className="rounded-xl border-sky-200">
                <SelectValue placeholder="Select service category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Caption (optional, applies to all)</label>
          <Input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Before & after roof cleaning"
            data-testid="photo-caption-input"
            className="rounded-xl border-sky-200"
          />
        </div>
        {previews.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500">{previews.length} photo{previews.length > 1 ? 's' : ''} ready</span>
              <button onClick={clearAll} className="text-xs text-red-500 hover:text-red-700 font-medium" data-testid="photo-clear-all">Clear all</button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="photo-previews">
              {previews.map((src, i) => (
                <div key={i} className="relative inline-block">
                  <img src={src} alt={`Preview ${i + 1}`} className="h-24 w-24 rounded-xl object-cover border border-sky-100" />
                  <button
                    onClick={() => removeFile(i)}
                    data-testid={`photo-remove-preview-${i}`}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {uploading && (
          <div className="mb-4" data-testid="upload-progress">
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-sky-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-sky-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
              <span className="text-xs font-medium text-slate-600 whitespace-nowrap">
                {uploadProgress.current} / {uploadProgress.total}
              </span>
            </div>
          </div>
        )}
        <Button
          onClick={handleUpload}
          disabled={uploading || files.length === 0 || !selectedCategory}
          data-testid="photo-upload-btn"
          className="rounded-full bg-sky-700 hover:bg-sky-600"
        >
          {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : `Upload ${files.length > 1 ? `${files.length} Photos` : 'Photo'}`}
        </Button>
      </div>

      {/* Filter + photo grid */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['All', ...categories].map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(c)}
            data-testid={`photo-admin-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              filterCat === c ? 'bg-sky-700 text-white' : 'bg-sky-50 text-slate-600 hover:bg-sky-100 border border-sky-100'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-slate-400 text-center py-8">No photos uploaded yet. Upload your first photo above.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-photos-grid">
          {filtered.map((photo) => (
            <div key={photo.id} className="relative group rounded-xl overflow-hidden border border-sky-100 bg-sky-50" data-testid={`admin-photo-${photo.id}`}>
              <img
                src={`${API}/photos/serve/${photo.id}`}
                alt={photo.caption || photo.original_filename}
                className="w-full h-40 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <button
                  onClick={() => handleDelete(photo.id)}
                  data-testid={`admin-delete-photo-${photo.id}`}
                  className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="p-2">
                <span className="text-xs font-medium text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full">{photo.category}</span>
                {photo.caption && <p className="text-xs text-slate-500 mt-1 truncate">{photo.caption}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ThumbnailPicker({ value, onChange }) {
  const [photos, setPhotos] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchPhotos = useCallback(() => {
    axios.get(`${API}/admin/photos`, { headers: getAuthHeaders(), withCredentials: true })
      .then((res) => setPhotos(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { if (expanded) fetchPhotos(); }, [expanded, fetchPhotos]);

  const handleDeviceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'Uncategorized');
    formData.append('caption', file.name.replace(/\.[^.]+$/, ''));
    try {
      const res = await axios.post(`${API}/admin/photos/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      onChange(`${API}/photos/serve/${res.data.id}`);
      fetchPhotos();
      toast.success('Thumbnail uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div data-testid="thumbnail-picker">
      <label className="text-sm font-medium text-slate-700 mb-1 block">Thumbnail Photo</label>
      <div className="flex items-center gap-3 mb-2">
        <img src={value} alt="Thumbnail preview" className="w-20 h-20 rounded-xl object-cover border border-sky-100 flex-shrink-0" />
        <div className="flex flex-col gap-1.5">
          <button type="button" onClick={() => setExpanded(!expanded)} data-testid="thumbnail-gallery-toggle" className="text-sky-700 text-xs font-medium hover:underline flex items-center gap-1">
            <Image className="w-3.5 h-3.5" /> {expanded ? 'Hide gallery' : 'Choose from gallery'}
          </button>
          <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all ${uploading ? 'bg-slate-100 text-slate-400' : 'bg-sky-100 text-sky-700 hover:bg-sky-200'}`}>
            <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Upload new photo'}
            <input type="file" accept="image/*" onChange={handleDeviceUpload} disabled={uploading} data-testid="thumbnail-upload-input" className="hidden" />
          </label>
        </div>
      </div>
      {expanded && (
        <div className="border border-sky-100 rounded-xl p-3 bg-sky-50/50 grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-40 overflow-y-auto" data-testid="thumbnail-gallery-grid">
          {photos.map((p) => {
            const url = `${API}/photos/serve/${p.id}`;
            return (
              <button key={p.id} type="button" onClick={() => { onChange(url); setExpanded(false); }}
                className={`relative rounded-lg overflow-hidden h-16 border-2 transition-all ${value === url ? 'border-sky-600 ring-2 ring-sky-300' : 'border-transparent hover:border-sky-200'}`}>
                <img src={url} alt={p.caption || ''} className="w-full h-full object-cover" />
              </button>
            );
          })}
          {photos.length === 0 && <p className="text-xs text-slate-400 col-span-full">No gallery photos yet.</p>}
        </div>
      )}
    </div>
  );
}

function ServiceEditDialog({ service, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (service) {
      setForm({
        name: service.name,
        shortDesc: service.shortDesc,
        fullDesc: service.fullDesc,
        image: service.image,
        pricingNote: service.pricingNote || '',
        pricing: service.pricing.map((p) => ({ ...p })),
        features: [...service.features],
      });
    }
  }, [service]);

  if (!open || !form) return null;

  const updatePricing = (i, field, val) => {
    const next = [...form.pricing];
    next[i] = { ...next[i], [field]: val };
    setForm({ ...form, pricing: next });
  };
  const addPricing = () => setForm({ ...form, pricing: [...form.pricing, { label: '', price: '' }] });
  const removePricing = (i) => setForm({ ...form, pricing: form.pricing.filter((_, idx) => idx !== i) });

  const updateFeature = (i, val) => {
    const next = [...form.features];
    next[i] = val;
    setForm({ ...form, features: next });
  };
  const addFeature = () => setForm({ ...form, features: [...form.features, ''] });
  const removeFeature = (i) => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    const validPricing = form.pricing.filter((p) => p.label.trim() && p.price.trim());
    const validFeatures = form.features.filter((f) => f.trim());
    if (!form.name.trim() || !form.shortDesc.trim() || !form.fullDesc.trim() || validPricing.length === 0) {
      toast.error('Title, both descriptions, and at least one price option are required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/services/${service.slug}`, {
        name: form.name,
        shortDesc: form.shortDesc,
        fullDesc: form.fullDesc,
        image: form.image,
        pricing: validPricing,
        pricingNote: form.pricingNote || null,
        features: validFeatures,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Service updated');
      onSaved();
      onClose();
    } catch { toast.error('Failed to update service'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="service-edit-dialog">
        <DialogHeader><DialogTitle>Edit {service.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ThumbnailPicker value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Title</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-edit-title" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Short Description <span className="text-slate-400 font-normal">(homepage card)</span></label>
            <Textarea value={form.shortDesc} onChange={(e) => setForm({ ...form, shortDesc: e.target.value })} rows={2} data-testid="service-edit-short-desc" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Full Description <span className="text-slate-400 font-normal">(service page)</span></label>
            <Textarea value={form.fullDesc} onChange={(e) => setForm({ ...form, fullDesc: e.target.value })} rows={4} data-testid="service-edit-full-desc" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Pricing Options</label>
            <div className="space-y-2">
              {form.pricing.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={p.label} onChange={(e) => updatePricing(i, 'label', e.target.value)} placeholder="Label" data-testid={`service-edit-price-label-${i}`} className="flex-1" />
                  <Input value={p.price} onChange={(e) => updatePricing(i, 'price', e.target.value)} placeholder="$Price" data-testid={`service-edit-price-value-${i}`} className="w-24" />
                  <Button type="button" variant="outline" size="icon" onClick={() => removePricing(i)} data-testid={`service-edit-price-remove-${i}`}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addPricing} data-testid="service-edit-add-price" className="text-sky-700 text-xs font-medium hover:underline mt-2 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add price option</button>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Pricing Note <span className="text-slate-400 font-normal">(optional)</span></label>
            <Input value={form.pricingNote} onChange={(e) => setForm({ ...form, pricingNote: e.target.value })} data-testid="service-edit-pricing-note" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Features <span className="text-slate-400 font-normal">("What's Included")</span></label>
            <div className="space-y-2">
              {form.features.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={f} onChange={(e) => updateFeature(i, e.target.value)} data-testid={`service-edit-feature-${i}`} className="flex-1" />
                  <Button type="button" variant="outline" size="icon" onClick={() => removeFeature(i)} data-testid={`service-edit-feature-remove-${i}`}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addFeature} data-testid="service-edit-add-feature" className="text-sky-700 text-xs font-medium hover:underline mt-2 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add feature</button>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="service-edit-save-btn" className="w-full rounded-full bg-sky-700 hover:bg-sky-600">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SiteSettingsTab() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);

  const fetchServices = useCallback(() => {
    axios.get(`${API}/services`).then((res) => setServices(res.data)).catch(() => toast.error('Failed to load services'));
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  return (
    <div data-testid="admin-site-settings-tab">
      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Edit Service Pages</h3>
      <p className="text-slate-400 text-sm mb-4">Update the title, description, thumbnail photo, pricing, and feature list shown on each service page.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.slug} className="bg-white rounded-2xl border border-sky-100 shadow-sm overflow-hidden" data-testid={`site-settings-service-${s.slug}`}>
            <img src={s.image} alt={s.name} className="w-full h-32 object-cover" />
            <div className="p-4">
              <h4 className="font-heading font-semibold text-slate-900 mb-1">{s.name}</h4>
              <p className="text-slate-400 text-xs mb-3 line-clamp-2">{s.shortDesc}</p>
              <Button onClick={() => setEditing(s)} size="sm" variant="outline" data-testid={`edit-service-btn-${s.slug}`} className="rounded-full border-sky-200 text-sky-700 w-full">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            </div>
          </div>
        ))}
      </div>
      <ServiceEditDialog service={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={fetchServices} />
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState('bookings');
  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [stats, setStats] = useState({ reviews: 0, contacts: 0, invoices: 0, unpaid_invoices: 0, paid_invoices: 0, bookings: 0, upcoming_bookings: 0, estimates: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const opts = { headers, withCredentials: true };
      const [revRes, conRes, invRes, bookRes, estRes, statRes] = await Promise.all([
        axios.get(`${API}/admin/reviews`, opts),
        axios.get(`${API}/admin/contacts`, opts),
        axios.get(`${API}/admin/invoices`, opts),
        axios.get(`${API}/admin/bookings`, opts),
        axios.get(`${API}/admin/estimates`, opts),
        axios.get(`${API}/admin/stats`, opts),
      ]);
      setReviews(revRes.data);
      setContacts(conRes.data);
      setInvoices(invRes.data);
      setBookings(bookRes.data);
      setEstimates(estRes.data);
      setStats(statRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, navigate]);

  const handleDelete = async (type, id) => {
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`${type === 'reviews' ? 'Review' : type === 'contacts' ? 'Contact' : type === 'bookings' ? 'Booking' : type === 'estimates' ? 'Estimate' : 'Invoice'} deleted`);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders(), withCredentials: true }); } catch {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-slate-400">Loading dashboard...</p></div>;

  return (
    <div data-testid="admin-dashboard" className="py-8">
      <Toaster position="top-right" richColors />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-400 text-sm mt-1">Manage invoices, reviews, and contacts</p>
          </div>
          <Button onClick={handleLogout} variant="outline" data-testid="admin-logout-btn" className="rounded-full border-sky-200 text-slate-600 hover:text-red-600 hover:border-red-200">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatCard icon={CalendarDays} label="Upcoming" value={stats.upcoming_bookings} color="bg-sky-600" />
          <StatCard icon={FileText} label="Invoices" value={stats.invoices} color="bg-slate-500" />
          <StatCard icon={DollarSign} label="Unpaid" value={stats.unpaid_invoices} color="bg-amber-500" />
          <StatCard icon={Star} label="Reviews" value={stats.reviews} color="bg-violet-500" />
          <StatCard icon={MessageSquare} label="Contacts" value={stats.contacts} color="bg-emerald-500" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="admin-tabs">
          {[
            { key: 'bookings', icon: CalendarDays, label: 'Bookings', count: bookings.length },
            { key: 'estimates', icon: ClipboardList, label: 'Estimates', count: estimates.length },
            { key: 'invoices', icon: FileText, label: 'Invoices', count: invoices.length },
            { key: 'reviews', icon: Star, label: 'Reviews', count: reviews.length },
            { key: 'contacts', icon: MessageSquare, label: 'Contacts', count: contacts.length },
            { key: 'photos', icon: Image, label: 'Photos' },
            { key: 'settings', icon: Settings, label: 'Site Settings' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                tab === t.key ? 'bg-sky-700 text-white shadow-md' : 'bg-sky-50 text-slate-600 hover:bg-sky-100'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label} {t.count !== undefined ? `(${t.count})` : ''}
            </button>
          ))}
        </div>

        {tab === 'bookings' && <BookingsTab bookings={bookings} onDelete={(id) => handleDelete('bookings', id)} onRefresh={fetchData} />}
        {tab === 'estimates' && <EstimatesTab estimates={estimates} onDelete={(id) => handleDelete('estimates', id)} onRefresh={fetchData} />}
        {tab === 'invoices' && <InvoicesTab invoices={invoices} onDelete={(id) => handleDelete('invoices', id)} onRefresh={fetchData} />}
        {tab === 'reviews' && <ReviewsTab reviews={reviews} onDelete={(id) => handleDelete('reviews', id)} />}
        {tab === 'contacts' && <ContactsTab contacts={contacts} onDelete={(id) => handleDelete('contacts', id)} />}
        {tab === 'photos' && <PhotosTab />}
        {tab === 'settings' && <SiteSettingsTab />}
      </div>
    </div>
  );
}
